import MuiTextField, {
  type TextFieldProps as MuiTextFieldProps,
} from '@mui/material/TextField';
import IconButton from '@mui/material/IconButton';
import Skeleton from '@mui/material/Skeleton';
import { Iconify } from '@mfw/components';
import classNames from 'classnames';
import omit from 'lodash/omit';
import React from 'react';

import type { TextFieldType } from './type';
import type { FieldProps } from '../types';

import { HelperMessage } from '../../HelperMessage';
import { ReadOnlyField } from '../../ReadOnlyField';
import { ErrorMessage } from '../../ErrorMessage';

export type TextFieldProps<TType = TextFieldType> = {
  onChange?: (
    e: TType extends 'textarea'
      ? React.ChangeEvent<HTMLTextAreaElement>
      : React.ChangeEvent<HTMLInputElement>,
  ) => void;
  /**
   * Virtual keyboard the device should offer. MUI spreads unknown props onto
   * the wrapping `FormControl`, so this has to travel through `inputProps` to
   * reach the native input.
   */
  inputMode?: React.InputHTMLAttributes<HTMLInputElement>['inputMode'];
  startAdornment?: React.ReactNode;
  endAdornment?: React.ReactNode;
  defaultValue?: string;
  withoutIcon?: boolean;
  placeholder?: string;
  type?: TextFieldType;
  multiline?: boolean;
  rows?: number;
  minRows?: number;
  maxRows?: number;
} & Pick<MuiTextFieldProps, 'classes' | 'onBlur' | 'onKeyDown'> &
  FieldProps;

const TextField = React.forwardRef(
  (
    {
      className,
      classes,
      'data-testid': testId,
      defaultValue,
      errors,
      fullWidth,
      helperText,
      inputMode,
      loading,
      maxRows,
      minRows,
      multiline,
      name,
      readOnly,
      rows,
      type = 'text',
      value,
      withoutIcon,
      ...props
    }: TextFieldProps,
    ref: React.ForwardedRef<HTMLInputElement>,
  ) => {
    const [localType, setLocalType] = React.useState(type);
    if (loading) {
      return (
        <Skeleton
          className={classNames(className, { 'w-full': fullWidth })}
          variant="rounded"
          height={58}
        />
      );
    }

    if (readOnly) {
      return (
        <ReadOnlyField
          startAdornment={props?.startAdornment}
          endAdornment={props?.endAdornment}
          value={value ?? defaultValue}
          label={props?.label ?? ''}
          multiline={multiline}
          className={className}
          fullWidth={fullWidth}
          errors={errors}
          type={type}
        />
      );
    }
    return (
      <div className={classNames(className, { 'w-full': fullWidth })}>
        <MuiTextField
          {...omit(props, ['endAdornment', 'startAdornment'])}
          InputProps={{
            classes: {
              inputAdornedEnd: 'px-2',
              inputAdornedStart: 'px-2',
            },
            endAdornment:
              type === 'password' ? (
                <IconButton
                  onClick={() =>
                    setLocalType((prev) =>
                      prev === 'password' ? 'text' : 'password',
                    )
                  }
                  edge="end"
                >
                  <Iconify
                    icon={localType === 'password' ? 'mdi:eye' : 'mdi:eye-off'}
                    size="small"
                  />
                </IconButton>
              ) : (
                props?.endAdornment
              ),
            startAdornment: props?.startAdornment,
          }}
          inputProps={{ 'data-testid': testId, inputMode }}
          onFocus={(e) => e.target.select()}
          data-testid={`text-field--${testId}`}
          placeholder={props?.placeholder}
          rows={rows ?? (minRows || maxRows ? undefined : multiline ? 3 : 1)}
          minRows={multiline ? minRows : undefined}
          maxRows={multiline ? maxRows : undefined}
          error={Boolean(errors)}
          fullWidth={fullWidth}
          multiline={multiline}
          variant="outlined"
          classes={classes}
          type={localType}
          inputRef={ref}
          value={value}
          name={name}
          ref={ref}
        />
        {helperText && (
          <HelperMessage className="mt-1 ml-2">{helperText}</HelperMessage>
        )}
        {errors && (
          <ErrorMessage
            withoutIcon={withoutIcon}
            className="mt-1"
            errors={errors}
          />
        )}
      </div>
    );
  },
);

TextField.displayName = 'TextField';

export default TextField;
